import { Link } from "react-router-dom";
import { Instagram, Twitter, Mail, Youtube } from "lucide-react";
import { BRAND, NAV_LINKS, PARENT } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";

const LEGAL_LINKS = [
    { key: "help", to: "/legal/help" },
    { key: "privacy", to: "/legal/privacy" },
    { key: "terms", to: "/legal/terms" },
    { key: "medicalSources", to: "/legal/medical-sources" },
    { key: "healthDisclaimer", to: "/legal/health-disclaimer" },
    { key: "account", to: "/account" },
    { key: "contact", to: "/legal/contact" },
];

export const Footer = () => {
    const { t } = useLang();

    return (
        <footer
            data-testid="site-footer"
            className="relative bg-black border-t border-white/5 pt-2 pb-3 overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr] gap-x-8 gap-y-2 mb-2 items-center">
                    <div className="flex flex-col items-start">
                        <img
                            src={BRAND.logo}
                            alt="606FITNESS"
                            className="h-64 sm:h-80 lg:h-96 w-auto object-contain -my-16 sm:-my-20 lg:-my-24"
                        />
                        <p className="text-neutral-400 text-xs sm:text-sm leading-relaxed max-w-sm">
                            {t.footer.tagline}
                        </p>
                    </div>

                    <div>
                        <h4 className="text-white font-bold mb-3 text-xs uppercase tracking-widest">
                            {t.footer.quickLinks}
                        </h4>
                        <ul className="space-y-2">
                            {NAV_LINKS.map((l) => (
                                <li key={l.href}>
                                    <a
                                        href={`/${l.href}`}
                                        data-testid={`footer-nav-${l.href.replace("#", "")}`}
                                        className="text-neutral-400 hover:text-[var(--primary)] transition-colors text-xs sm:text-sm"
                                    >
                                        {t.nav[l.key]}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h4 className="text-white font-bold mb-3 text-xs uppercase tracking-widest">
                            {t.footer.supportLegal}
                        </h4>
                        <ul className="space-y-2">
                            {LEGAL_LINKS.map((l) => (
                                <li key={l.key}>
                                    <Link
                                        to={l.to}
                                        data-testid={`footer-legal-${l.key}`}
                                        className="text-neutral-400 hover:text-[var(--primary)] transition-colors text-xs sm:text-sm text-start inline-block"
                                    >
                                        {t.footer.legal[l.key]}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="pt-3 border-t border-white/5 flex flex-col gap-4">
                    {/* Ownership & affiliation — refined credit panel */}
                    <div
                        data-testid="footer-parent-companies"
                        className="relative rounded-2xl border border-white/5 bg-gradient-to-b from-white/[0.02] to-transparent p-5 sm:p-6"
                    >
                        <div className="grid grid-cols-2 gap-4 sm:gap-8 items-center">
                            <a
                                href="#"
                                data-testid="footer-owned-by"
                                className="group flex flex-col items-center text-center gap-2 transition-transform hover:-translate-y-1"
                            >
                                <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-display">
                                    {t.footer.ownedBy}
                                </span>
                                <div className="relative h-14 sm:h-20 flex items-center justify-center w-full">
                                    <img
                                        src={PARENT.cloudGm.logo}
                                        alt={PARENT.cloudGm.name}
                                        className="h-full w-auto object-contain opacity-80 group-hover:opacity-100 transition-opacity"
                                    />
                                </div>
                                <span className="text-[10px] sm:text-xs tracking-widest text-neutral-400 group-hover:text-white transition-colors font-bold">
                                    {PARENT.cloudGm.name}
                                </span>
                            </a>

                            {/* Decorative center divider with orange accent */}
                            <div className="hidden" />

                            <a
                                href="#"
                                data-testid="footer-part-of"
                                className="group flex flex-col items-center text-center gap-2 transition-transform hover:-translate-y-1"
                            >
                                <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-display">
                                    {t.footer.partOf}
                                </span>
                                <div className="relative h-14 sm:h-20 flex items-center justify-center w-full">
                                    <img
                                        src={PARENT.asb606.logo}
                                        alt={PARENT.asb606.name}
                                        className="h-full w-auto object-contain opacity-80 group-hover:opacity-100 transition-opacity"
                                    />
                                </div>
                                <span className="text-[10px] sm:text-xs tracking-widest text-neutral-400 group-hover:text-white transition-colors font-bold">
                                    {PARENT.asb606.name}
                                </span>
                            </a>
                        </div>
                        {/* Vertical divider line in middle */}
                        <div className="absolute top-6 bottom-6 left-1/2 w-px bg-gradient-to-b from-transparent via-[var(--primary)]/40 to-transparent pointer-events-none" />
                    </div>

                    {/* Bottom utility row */}
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-3 border-t border-white/5">
                        <div className="flex gap-2.5">
                            {[
                                { Icon: Instagram, href: "#", label: "instagram" },
                                { Icon: Twitter, href: "#", label: "twitter" },
                                { Icon: Youtube, href: "#", label: "youtube" },
                                { Icon: Mail, href: "mailto:support@606fitness.com", label: "mail" },
                            ].map(({ Icon, href, label }) => (
                                <a
                                    key={label}
                                    href={href}
                                    data-testid={`social-${label}`}
                                    className="w-8 h-8 flex items-center justify-center rounded-full border border-white/10 text-neutral-300 hover:text-black hover:bg-[var(--primary)] hover:border-[var(--primary)] transition-all"
                                    aria-label={label}
                                >
                                    <Icon className="w-3.5 h-3.5" />
                                </a>
                            ))}
                        </div>
                        <div className="flex flex-col sm:flex-row items-center gap-2 text-[10px] sm:text-xs text-neutral-500">
                            <p>
                                © {new Date().getFullYear()} 606FITNESS.{" "}
                                {t.footer.copyright}
                            </p>
                            <span className="hidden sm:inline text-neutral-700">•</span>
                            <p className="font-display tracking-widest text-neutral-600">
                                {t.footer.brandTag}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};
